import { useEffect, useRef, useState } from 'react'
import { getAllPlanetData, getSarvashtakavarga, REQUIRED_SHADBALA, SAV_PLANETS, SIGN_ORDER } from '@astro/vedastro'
import { Badge, PillGroup, PLANET_GLYPHS, StudioError, TableSkeleton } from '../StudioBits'

const VIEWS = [
  { key: 'shadbala', label: 'Shadbala', icon: '⚖️' },
  { key: 'ashtakavarga', label: 'Ashtakavarga', icon: '🔢' },
]

const SIGN_GLYPHS = ['♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓']

const fmt1 = (n) => (n == null ? '—' : Number(n).toFixed(1))

function ShadbalaTable({ planets }) {
  // Shadbala is defined for the seven classical grahas only.
  const rows = SAV_PLANETS.map((name) => planets.find((p) => p.name === name)).filter(Boolean)
  return (
    <>
      <div className="ks-table-wrap">
        <table className="ks-table">
          <thead>
            <tr>
              <th>Graha</th><th>Total (Virupas)</th><th>Required</th><th>Power</th><th>Sthana</th>
              <th>Dig</th><th>Kala</th><th>Chesta</th><th>Naisargika</th><th>Drik</th><th>Verdict</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => {
              const required = REQUIRED_SHADBALA[p.name]
              const ratio = required ? p.shadbala / required : 0
              return (
                <tr key={p.name}>
                  <td>
                    <span className="ks-planet-cell">
                      <span className="ks-planet-glyph">{PLANET_GLYPHS[p.name]}</span>
                      {p.name}
                    </span>
                  </td>
                  <td className="ks-mono"><strong>{fmt1(p.shadbala)}</strong></td>
                  <td className="ks-mono ks-dim">{required}</td>
                  <td>
                    <span className="ks-meter" title={`${Math.round(ratio * 100)}% of required`}>
                      <span
                        className={`ks-meter-fill${ratio >= 1 ? '' : ' low'}`}
                        style={{ width: `${Math.min(ratio, 1.5) / 1.5 * 100}%` }}
                      />
                    </span>
                  </td>
                  <td className="ks-mono">{fmt1(p.balas.sthana)}</td>
                  <td className="ks-mono">{fmt1(p.balas.dig)}</td>
                  <td className="ks-mono">{fmt1(p.balas.kala)}</td>
                  <td className="ks-mono">{p.balas.chesta == null ? <span className="ks-dim">—</span> : fmt1(p.balas.chesta)}</td>
                  <td className="ks-mono">{fmt1(p.balas.naisargika)}</td>
                  <td className="ks-mono">{fmt1(p.balas.drik)}</td>
                  <td><Badge tone={p.strongInShadbala ? 'green' : 'red'}>{p.strongInShadbala ? 'Strong' : 'Weak'}</Badge></td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <p className="ks-note">
        Shadbala sums six classical strength sources per graha (in Virupas). A planet at or above its required
        minimum is counted strong and delivers its promises more freely. Chesta bala does not apply to Sun and Moon.
      </p>
    </>
  )
}

function AshtakavargaTable({ sav }) {
  const best = Math.max(...sav.sav.rows)
  const worst = Math.min(...sav.sav.rows)
  return (
    <>
      <div className="ks-table-wrap">
        <table className="ks-table">
          <thead>
            <tr>
              <th>Sign</th>
              {SAV_PLANETS.map((p) => <th key={p} className="ks-center">{p}</th>)}
              <th className="ks-center">Asc</th>
              <th className="ks-center">Total</th>
            </tr>
          </thead>
          <tbody>
            {SIGN_ORDER.map((sign, i) => {
              const total = sav.sav.rows[i]
              return (
                <tr key={sign}>
                  <td>
                    <span className="ks-planet-cell">
                      <span className="ks-planet-glyph">{SIGN_GLYPHS[i]}</span>
                      {sign}
                    </span>
                  </td>
                  {SAV_PLANETS.map((p) => <td key={p} className="ks-center ks-mono">{sav.planets[p].rows[i]}</td>)}
                  <td className="ks-center ks-mono ks-dim">{sav.ascendant ? sav.ascendant.rows[i] : '—'}</td>
                  <td className="ks-center">
                    <Badge tone={total === best ? 'green' : total === worst ? 'red' : total >= 28 ? 'gold' : 'muted'}>
                      {total}
                    </Badge>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <p className="ks-note">
        Bindus (benefic points) each graha contributes to every sign — the Sarvashtakavarga total maps your strong
        and weak zones: {best} bindus is your best-supported sign, {worst} the most delicate. 28+ is above average.
        Transits through high-bindu signs tend to give better results.
      </p>
    </>
  )
}

/**
 * Strength systems — Shadbala (from the cached AllPlanetData call, no extra
 * API spend) and the Ashtakavarga bindu matrix (one cached call).
 */
export default function StrengthPart({ birth }) {
  const [view, setView] = useState('shadbala')
  const [status, setStatus] = useState('loading') // loading | error | done
  const [data, setData] = useState(null)
  const [errorMsg, setErrorMsg] = useState('')
  const reqRef = useRef(0)

  const load = () => {
    const my = ++reqRef.current
    setStatus('loading')
    const args = { dob: birth.dob, tob: birth.tob, location: birth.location }
    Promise.all([getAllPlanetData(args), getSarvashtakavarga(args)])
      .then(([planets, sav]) => {
        if (my !== reqRef.current) return
        setData({ planets, sav })
        setStatus('done')
      })
      .catch((err) => {
        if (my !== reqRef.current || err?.name === 'AbortError') return
        setErrorMsg(err?.message || 'We could not compute the strengths right now. Please try again.')
        setStatus('error')
      })
  }
  useEffect(load, [birth]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div className="ks-controls">
        <PillGroup ariaLabel="Strength system" options={VIEWS} value={view} onChange={setView} />
      </div>
      {status === 'loading' && <TableSkeleton rows={view === 'ashtakavarga' ? 12 : 7} cols={9} />}
      {status === 'error' && <StudioError text={errorMsg} onRetry={load} />}
      {status === 'done' && data && (
        view === 'shadbala' ? <ShadbalaTable planets={data.planets} /> : <AshtakavargaTable sav={data.sav} />
      )}
    </div>
  )
}
